import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Map as MapIcon, Lock, Unlock, Camera, Radio, Thermometer, Lightbulb, AlertTriangle, Zap, RefreshCw, DoorOpen, User } from "lucide-react";

const CAT_ICON = { lock: Lock, camera: Camera, sensor: Radio, thermostat: Thermometer, lighting: Lightbulb, access_controller: DoorOpen, energy_meter: Zap, gateway: Radio };

function roomTone(r) {
    if (r.alert_count > 0) return "border-destructive/60 bg-destructive/10 hover:bg-destructive/15";
    if (r.presence === "occupied") return "border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/15";
    if (r.presence === "vacant") return "border-border bg-secondary/40 hover:bg-secondary/70";
    return "border-border bg-card hover:bg-secondary/40";
}

export default function FloorMap() {
    const [data, setData] = useState(null);
    const [devices, setDevices] = useState([]);
    const [selected, setSelected] = useState(null);
    const [roomEvents, setRoomEvents] = useState([]);
    const [iso, setIso] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [fm, dv] = await Promise.all([api.get("/devices/floormap"), api.get("/devices")]);
            setData(fm.data);
            setDevices(dv.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load floor map");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const seed = async () => {
        try { const { data } = await api.post("/devices/seed-demo", {}); toast.success(`Seeded ${data.created} devices`); load(); }
        catch { toast.error("Seed failed"); }
    };

    const openRoom = async (room) => {
        setSelected(room);
        try { const { data } = await api.get(`/devices/events?room_id=${room.room_id}&limit=15`); setRoomEvents(data); }
        catch { setRoomEvents([]); }
    };

    const roomDevices = useMemo(() => selected ? devices.filter((d) => d.room_id === selected.room_id) : [], [selected, devices]);

    const command = async (deviceId, action) => {
        try { await api.post(`/devices/${deviceId}/command`, { action }); toast.success(action); await load(); if (selected) openRoom(selected); }
        catch { toast.error("Command failed"); }
    };
    const simulateRoom = async (room) => {
        const devs = devices.filter((d) => d.room_id === room.room_id);
        if (!devs.length) return toast.error("No devices in room");
        try { await api.post(`/devices/${devs[Math.floor(Math.random() * devs.length)].id}/simulate`, {}); await load(); openRoom(room); toast.success("Simulated activity"); }
        catch { toast.error("Simulate failed"); }
    };

    const summary = data?.summary;
    const empty = data && data.floors.length > 0 && summary?.devices === 0;

    return (
        <div className="space-y-5" data-testid="floor-map-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><MapIcon className="w-3.5 h-3.5" /> Operations</div>
                    <h1 className="text-2xl font-semibold tracking-tight mt-1">Live Floor Map</h1>
                    <p className="text-muted-foreground mt-1 text-sm max-w-2xl">Rooms by floor with live presence, device coverage and security alerts. Presence is derived from motion / door / access events (mock or real webhooks).</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">3D</span><Switch checked={iso} onCheckedChange={setIso} data-testid="floormap-3d-toggle" /></div>
                    <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
                </div>
            </div>

            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="floormap-summary">
                    {[["Rooms", summary.rooms], ["Occupied", summary.occupied], ["Devices", summary.devices], ["Open alerts", summary.alerts]].map(([k, v]) => (
                        <Card key={k}><CardContent className="py-3">
                            <div className="text-xs text-muted-foreground">{k}</div>
                            <div className={`text-2xl font-semibold ${k === "Open alerts" && v > 0 ? "text-destructive" : ""}`}>{v}</div>
                        </CardContent></Card>
                    ))}
                </div>
            )}

            {empty && (
                <Card><CardContent className="py-8 text-center space-y-3">
                    <div className="text-sm text-muted-foreground">No devices yet. Seed a demo fleet (locks + sensors on every room, corridor cameras) to see the map come alive.</div>
                    <Button onClick={seed} data-testid="floormap-seed"><Zap className="w-4 h-4 mr-1" /> Seed demo fleet</Button>
                </CardContent></Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
                    {data?.floors.map((f) => (
                        <div key={f.floor}>
                            <div className="text-sm font-medium mb-2">Floor {f.floor} <span className="text-muted-foreground">· {f.rooms.length} rooms</span></div>
                            <div className={iso ? "transition-transform duration-300" : ""} style={iso ? { transform: "perspective(1200px) rotateX(52deg) rotateZ(-40deg)", transformStyle: "preserve-3d" } : {}}>
                                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                                    {f.rooms.map((r) => (
                                        <button key={r.room_id} onClick={() => openRoom(r)} data-testid={`floormap-room-${r.number}`}
                                            className={`relative rounded-lg border p-2 text-left transition-colors ${roomTone(r)} ${selected?.room_id === r.room_id ? "ring-2 ring-primary" : ""}`}
                                            style={iso ? { boxShadow: "0 10px 0 -2px rgba(0,0,0,0.08)" } : {}}>
                                            <div className="flex items-center justify-between">
                                                <span className="font-semibold text-sm">{r.number}</span>
                                                {r.presence === "occupied" && <User className="w-3.5 h-3.5 text-emerald-600" />}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground truncate">{r.type}</div>
                                            <div className="flex items-center gap-1 mt-1">
                                                {r.device_categories.slice(0, 4).map((c) => { const I = CAT_ICON[c] || Radio; return <I key={c} className="w-3 h-3 text-muted-foreground" />; })}
                                                {r.alert_count > 0 && <span className="ml-auto inline-flex items-center gap-0.5 text-destructive text-[10px] font-medium"><AlertTriangle className="w-3 h-3" />{r.alert_count}</span>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground pt-1">
                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/40 border border-emerald-500/50" /> occupied</span>
                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-secondary border border-border" /> vacant</span>
                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-destructive/30 border border-destructive/60" /> alert</span>
                    </div>
                </div>

                {/* Room detail panel */}
                <div>
                    <Card className="sticky top-4" data-testid="floormap-room-panel">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">{selected ? `Room ${selected.number}` : "Select a room"}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {!selected && <div className="text-sm text-muted-foreground">Click any room to see its devices and recent events.</div>}
                            {selected && (
                                <>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge variant="outline" className={selected.presence === "occupied" ? "bg-emerald-500/12 text-emerald-600 border-emerald-500/30" : ""}>{selected.presence}</Badge>
                                        <Badge variant="outline">{selected.status}</Badge>
                                        {selected.alert_count > 0 && <Badge variant="outline" className="bg-destructive/12 text-destructive border-destructive/30"><AlertTriangle className="w-3 h-3 mr-1" />{selected.alert_count} alert</Badge>}
                                        <Button size="sm" variant="outline" className="ml-auto" onClick={() => simulateRoom(selected)} data-testid="floormap-simulate"><Zap className="w-3.5 h-3.5 mr-1" />Simulate</Button>
                                    </div>

                                    <div>
                                        <div className="text-xs font-medium mb-1">Devices ({roomDevices.length})</div>
                                        <div className="space-y-1.5">
                                            {roomDevices.map((d) => { const I = CAT_ICON[d.category] || Radio; return (
                                                <div key={d.id} className="flex items-center gap-2 text-sm rounded-lg border border-border px-2 py-1.5">
                                                    <I className="w-4 h-4 text-muted-foreground" />
                                                    <div className="min-w-0">
                                                        <div className="truncate">{d.name}</div>
                                                        <div className="text-[10px] text-muted-foreground">{d.vendor} · {d.protocol} · {d.status}</div>
                                                    </div>
                                                    {d.category === "lock" && (
                                                        <Button size="sm" variant="ghost" className="ml-auto h-7 px-2" onClick={() => command(d.id, d.state?.locked === false ? "lock" : "unlock")} data-testid={`floormap-lock-${d.id}`}>
                                                            {d.state?.locked === false ? <><Lock className="w-3.5 h-3.5 mr-1" />Lock</> : <><Unlock className="w-3.5 h-3.5 mr-1" />Unlock</>}
                                                        </Button>
                                                    )}
                                                </div>
                                            ); })}
                                            {roomDevices.length === 0 && <div className="text-xs text-muted-foreground">No devices bound to this room.</div>}
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-xs font-medium mb-1">Recent events</div>
                                        <div className="space-y-1 max-h-52 overflow-y-auto">
                                            {roomEvents.map((e) => (
                                                <div key={e.id} className="flex items-center gap-2 text-xs">
                                                    <span className={`w-1.5 h-1.5 rounded-full ${e.severity === "critical" ? "bg-destructive" : e.severity === "warning" ? "bg-amber-500" : "bg-muted-foreground/50"}`} />
                                                    <span className="font-medium">{e.type}</span>
                                                    <span className="text-muted-foreground ml-auto">{(e.ts || "").slice(11, 16)}</span>
                                                </div>
                                            ))}
                                            {roomEvents.length === 0 && <div className="text-xs text-muted-foreground">No events yet.</div>}
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
